import React from 'react';
import '../css/TermsOfService.css';
import { FileText, Mail, Scale, Shield, AlertCircle, Users, CreditCard, Lock } from 'lucide-react';

const TermsOfService: React.FC = () => {
  return (
    <div className="terms-section">
      <div className="terms-content">
        <div className="terms-header">
          <FileText size={48} className="terms-icon" />
          <h1 className="terms-title">Terms of Service</h1>
          <p className="terms-intro">
            Please read these terms carefully before using our services. By accessing or using our website, 
            you agree to be bound by these terms and conditions.
          </p>
        </div>

        <div className="terms-sections">
          {/* About the Website */}
          <section className="terms-section-block">
            <div className="section-icon">
              <FileText size={32} />
            </div>
            <h2 className="section-title">About the Website</h2>
            <p className="section-text">
              Welcome to nimbli.io ("Website"). The Website provides an overview of the Nimbli AI products 
              and offerings ("Services").
            </p>
            <p className="section-text">
              The Website is operated by Nfinity Holdings Pty Ltd (ACN 680 638 760). Access to and use of 
              the Website, or any of its associated products or services, is provided by Nfinity Holdings 
              Pty Ltd ("we", "us", or "our").
            </p>
            <p className="section-text important">
              By using, browsing and/or reading the Website, you signify that you have read, understood, 
              and agree to be bound by these Terms. If you do not agree, you must cease using the Website 
              or any Services immediately.
            </p>
            <p className="section-text">
              We reserve the right to review and change these Terms at any time by updating this page. 
              Any changes take effect immediately upon publication.
            </p>
          </section>

          {/* Acceptance of Terms */}
          <section className="terms-section-block">
            <div className="section-icon">
              <Shield size={32} />
            </div>
            <h2 className="section-title">Acceptance of the Terms</h2>
            <p className="section-text">
              You accept these Terms by remaining on the Website. You may also accept them by clicking 
              "accept" or "agree" where this option is available on the Website.
            </p>
          </section>

          {/* Subscription */}
          <section className="terms-section-block">
            <div className="section-icon">
              <CreditCard size={32} />
            </div>
            <h2 className="section-title">Subscription to Use the Services</h2>
            <p className="section-text">
              To access certain Services, you must first purchase a Subscription and pay the applicable fee. 
              By purchasing a Subscription, you acknowledge that it is your responsibility to ensure the 
              chosen Subscription suits your needs.
            </p>
            <p className="section-text">
              Once payment is complete, you must register for an Account through the Website before accessing 
              the Services. During registration or continued use, you may be required to provide personal 
              details such as:
            </p>
            <ul className="terms-list">
              <li>Email address</li>
              <li>Preferred username</li>
              <li>Mailing address</li>
              <li>Telephone number</li>
              <li>Password</li>
            </ul>
            <p className="section-text">
              You warrant that any information you provide is accurate and up to date.
            </p>
            <p className="section-text">
              Upon completing registration, you become a Member, agreeing to be bound by these Terms. 
              You will have access to the Services for the period covered by your Subscription ("Subscription Period").
            </p>
            <div className="warning-box">
              <AlertCircle size={24} />
              <div>
                <p className="section-text" style={{ marginBottom: '0.5rem' }}>
                  You may not use the Services or accept these Terms if:
                </p>
                <ul className="terms-list">
                  <li>You are not of legal age to form a binding contract; or</li>
                  <li>You are barred from receiving Services under Australian or other applicable laws.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Member Obligations */}
          <section className="terms-section-block">
            <div className="section-icon">
              <Users size={32} />
            </div>
            <h2 className="section-title">Your Obligations as a Member</h2>
            <p className="section-text">As a Member, you agree to:</p>
            <ul className="terms-list">
              <li>Use the Services only for lawful purposes, following these Terms and applicable laws.</li>
              <li>Protect the confidentiality of your password and email address.</li>
              <li>Notify us immediately of any unauthorised use or security breach.</li>
              <li>Use the Website for personal use only, not for resale or redistribution.</li>
              <li>Avoid any illegal or unauthorised use, including collecting email addresses or framing/linking to the Website.</li>
            </ul>
            <p className="section-text">
              Commercial advertisements, affiliate links, and solicitation are not allowed. We may remove 
              them without notice and terminate Services for such violations.
            </p>
          </section>

          {/* Payment */}
          <section className="terms-section-block">
            <div className="section-icon">
              <CreditCard size={32} />
            </div>
            <h2 className="section-title">Payment</h2>
            <p className="section-text">
              All payments for Services are processed by our chosen provider. By making a payment, you 
              agree to the payment provider's terms and conditions.
            </p>
            <p className="section-text">
              If your payment fails or is denied, you remain responsible for any related fees or charges.
            </p>
            <p className="section-text">
              We may vary Subscription Fees at any time. Changes take effect after your current Subscription 
              Period ends.
            </p>
          </section>

          {/* Refund Policy */}
          <section className="terms-section-block">
            <div className="section-icon">
              <CreditCard size={32} />
            </div>
            <h2 className="section-title">Refund Policy</h2>
            <p className="section-text">
              We may provide a refund if we are unable to continue providing the Services, or at our sole 
              discretion if it is reasonable under the circumstances. Refunds will be for the unused portion 
              of the Subscription Fee.
            </p>
            <p className="section-text">
              These benefits apply in addition to your rights under the Australian Consumer Law.
            </p>
          </section>

          {/* Copyright */}
          <section className="terms-section-block">
            <div className="section-icon">
              <Lock size={32} />
            </div>
            <h2 className="section-title">Copyright and Intellectual Property</h2>
            <p className="section-text">
              All material on the Website, including text, graphics, logos, code, and design elements, 
              is protected by copyright under Australian and international law. Unless stated otherwise, 
              all rights belong to Nfinity Holdings Pty Ltd or its contributors.
            </p>
            <p className="section-text">You are granted a non-exclusive, royalty-free licence to:</p>
            <ul className="terms-list">
              <li>Use the Website pursuant to these Terms.</li>
              <li>Copy and store Website material in your device's cache memory.</li>
              <li>Print pages for personal, non-commercial use.</li>
            </ul>
            <p className="section-text">
              You may not otherwise broadcast, republish, upload, post, or adapt any Website content 
              without written permission. All trademarks, trade names, and logos remain the property 
              of Nfinity Holdings Pty Ltd.
            </p>
          </section>

          {/* Privacy */}
          <section className="terms-section-block">
            <div className="section-icon">
              <Shield size={32} />
            </div>
            <h2 className="section-title">Privacy</h2>
            <p className="section-text">
              We take your privacy seriously. Any information provided through your use of the Website 
              or Services is governed by our Privacy Policy.
            </p>
          </section>

          {/* Disclaimer */}
          <section className="terms-section-block">
            <div className="section-icon">
              <AlertCircle size={32} />
            </div>
            <h2 className="section-title">General Disclaimer</h2>
            <p className="section-text">
              Nothing in these Terms limits or excludes rights guaranteed under the Australian Consumer Law.
            </p>
            <p className="section-text">To the extent permitted by law:</p>
            <ul className="terms-list">
              <li>All implied terms, warranties, and conditions are excluded.</li>
              <li>We are not liable for any indirect or consequential loss, loss of profit, or damage to goodwill.</li>
            </ul>
            <p className="section-text">
              Use of the Website and Services is at your own risk. Everything is provided "as is" and 
              "as available" without warranties of any kind.
            </p>
            <p className="section-text">We do not guarantee:</p>
            <ul className="terms-list">
              <li>Continuous or error-free operation.</li>
              <li>The accuracy or currency of information.</li>
              <li>The security of any linked sites.</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section className="terms-section-block">
            <div className="section-icon">
              <Scale size={32} />
            </div>
            <h2 className="section-title">Limitation of Liability</h2>
            <p className="section-text">
              Our total liability for any claim arising from these Terms or your use of the Services 
              will not exceed the resupply of the Services.
            </p>
            <p className="section-text">
              We are not liable for any direct or indirect damages, including loss of profit, goodwill, 
              or data, however caused.
            </p>
          </section>

          {/* Competitors */}
          <section className="terms-section-block">
            <div className="section-icon">
              <AlertCircle size={32} />
            </div>
            <h2 className="section-title">Competitors</h2>
            <p className="section-text">
              If you provide similar services for commercial gain, you are considered a competitor and 
              are not permitted to access or use any Website content. We may seek damages and compensation 
              for any unauthorised use.
            </p>
          </section>

          {/* Termination */}
          <section className="terms-section-block">
            <div className="section-icon">
              <AlertCircle size={32} />
            </div>
            <h2 className="section-title">Termination</h2>
            <p className="section-text">
              These Terms remain in effect until terminated by either you or us.
            </p>
            <p className="section-text">You may terminate by:</p>
            <ul className="terms-list">
              <li>Not renewing your Subscription before it expires.</li>
              <li>Providing 30 days' written notice via our Contact page.</li>
              <li>Closing your account.</li>
            </ul>
            <p className="section-text">We may terminate at any time if:</p>
            <ul className="terms-list">
              <li>You fail to renew your Subscription.</li>
              <li>You breach these Terms.</li>
              <li>Required by law.</li>
              <li>Continuing the Services is no longer commercially viable.</li>
            </ul>
            <p className="section-text">
              We may suspend or deny access without notice if your conduct harms our reputation or 
              violates any law.
            </p>
          </section>

          {/* Indemnity */}
          <section className="terms-section-block">
            <div className="section-icon">
              <Scale size={32} />
            </div>
            <h2 className="section-title">Indemnity</h2>
            <p className="section-text">
              You agree to indemnify Nfinity Holdings Pty Ltd, its affiliates, employees, agents, and 
              licensors from all claims, costs, or damages arising from:
            </p>
            <ul className="terms-list">
              <li>Your content;</li>
              <li>Your use or attempted use of the Website;</li>
              <li>Any breach of these Terms.</li>
            </ul>
          </section>

          {/* Dispute Resolution */}
          <section className="terms-section-block">
            <div className="section-icon">
              <Scale size={32} />
            </div>
            <h2 className="section-title">Dispute Resolution</h2>
            
            <div className="info-card">
              <h3 className="info-card-title">Compulsory</h3>
              <p className="section-text">
                No legal action may commence until this process is followed (except for urgent injunctions).
              </p>
            </div>

            <div className="info-card">
              <h3 className="info-card-title">Notice</h3>
              <p className="section-text">
                A party claiming a dispute must give written notice detailing the nature of the dispute 
                and desired outcome.
              </p>
            </div>

            <div className="info-card">
              <h3 className="info-card-title">Resolution</h3>
              <p className="section-text">
                Within 28 days, both parties must attempt to resolve the dispute in good faith. If unresolved, 
                a mediator may be appointed (e.g. via the Australian Mediation Association or Resolution Institute).
              </p>
              <p className="section-text">
                Mediation will take place in Sydney, Australia. Each party pays its own costs and shares 
                the mediator's fees equally.
              </p>
            </div>

            <div className="info-card">
              <h3 className="info-card-title">Termination of Mediation</h3>
              <p className="section-text">
                If two months pass without resolution, either party may request termination of mediation.
              </p>
            </div>
          </section>

          {/* Venue and Jurisdiction */}
          <section className="terms-section-block">
            <div className="section-icon">
              <Scale size={32} />
            </div>
            <h2 className="section-title">Venue and Jurisdiction</h2>
            <p className="section-text">
              These Services are intended for Australian residents. Any dispute shall be heard in the 
              courts of New South Wales, Australia.
            </p>
          </section>

          {/* Governing Law */}
          <section className="terms-section-block">
            <div className="section-icon">
              <Scale size={32} />
            </div>
            <h2 className="section-title">Governing Law</h2>
            <p className="section-text">
              These Terms are governed by the laws of New South Wales, Australia. All disputes will be 
              resolved under these laws.
            </p>
          </section>

          {/* Severance */}
          <section className="terms-section-block">
            <div className="section-icon">
              <Scale size={32} />
            </div>
            <h2 className="section-title">Severance</h2>
            <p className="section-text">
              If any part of these Terms is found void or unenforceable, that part will be severed and 
              the remainder will continue in effect.
            </p>
          </section>

          {/* Contact */}
          <section className="terms-section-block terms-contact-section">
            <div className="section-icon">
              <Mail size={32} />
            </div>
            <h2 className="section-title">Questions About These Terms?</h2>
            <p className="section-text">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <a href="mailto:getintouch@crikeytech.com" className="contact-email">
              <Mail size={20} />
              getintouch@crikeytech.com
            </a>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;